import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { MoveCustom } from '@repo/go-icons';
import { Checkbox } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';

import type { CameraState, VideoBroadcastWidgetState } from '../../../store/VideoBroadcastWidgetState';
import styles from '../../VideoBroadcastWidget.module.scss';

const CameraItem = observer(function CameraItem({
  camera,
  updateState,
  camName,
  visible,
}: {
  camera: CameraState;
  updateState(): void;
  camName: string;
  visible: boolean;
}) {
  const { setNodeRef, listeners, attributes, transform, isDragging, transition } = useSortable({
    id: camera.id,
    data: { item: camera },
  });

  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    transition,
  };

  return (
    <div className={styles.cameraItemWrapper} ref={setNodeRef} style={style}>
      <MoveCustom
        className={clsx(styles.cameraItemDragIcon, isDragging && styles.cameraItemDragIconDragging)}
        {...listeners}
        {...attributes}
      />
      <Checkbox
        initialValue={camera.selected}
        onChange={(value) => {
          camera.setSelected(value);
          updateState();
        }}
        className={styles.cameraItemCheckbox}
      />
      <span className={clsx(styles.cameraItemText, !visible && styles.cameraItemTextMuted)}>{camName}</span>
    </div>
  );
});

export const CamerasList = observer(function CamerasList({ widgetState }: { widgetState: VideoBroadcastWidgetState }) {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  return (
    <div className={styles.camerasListItemWrapper}>
      <DndContext onDragEnd={widgetState.onDragEnd} sensors={sensors}>
        <SortableContext strategy={verticalListSortingStrategy} items={widgetState.cameras}>
          {widgetState.cameras.map((camera) => (
            <CameraItem
              key={camera.id}
              camera={camera}
              updateState={widgetState.updateSelectedCams}
              camName={camera.data.cameraName ?? ''}
              visible={widgetState.isCameraVisible(camera)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
});
